import { useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useBusinessContext } from "@/providers/business-provider";
import {
  useStaffList,
  useCreateStaff,
  useDeactivateStaff,
  useReactivateStaff,
  useResetStaffPassword,
  useAssignCompanies,
  useStaffPlanLimit,
  type CreateStaffData,
} from "@/hooks/use-staff";
import {
  Users, Plus, UserCheck, UserX, KeyRound, Building2, Loader2, Mail, Shield, UserCircle,
} from "lucide-react";

export default function StaffListPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { businesses } = useBusinessContext();
  const { data: staff = [], isLoading, error: staffError } = useStaffList();
  const { data: planLimit } = useStaffPlanLimit();

  // Debug logging
  console.log("[Staff Page] user:", user?.email, "isStaff:", !!(user as any)?.isStaff, "staff count:", staff.length, "error:", staffError?.message);
  const createStaff = useCreateStaff();
  const deactivateStaff = useDeactivateStaff();
  const reactivateStaff = useReactivateStaff();
  const resetPassword = useResetStaffPassword();
  const assignCompanies = useAssignCompanies();

  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState<number | null>(null);
  const [showAssign, setShowAssign] = useState<number | null>(null);

  // Create form state
  const [form, setForm] = useState<CreateStaffData>({ email: "", password: "", firstName: "", lastName: "", role: "staff", companyIds: [] });
  const [newPassword, setNewPassword] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);

  const handleCreate = async () => {
    try {
      await createStaff.mutateAsync(form);
      toast({ title: "Staff member created" });
      setShowCreate(false);
      setForm({ email: "", password: "", firstName: "", lastName: "", role: "staff", companyIds: [] });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await deactivateStaff.mutateAsync(id);
      toast({ title: "Staff member deactivated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      await reactivateStaff.mutateAsync(id);
      toast({ title: "Staff member reactivated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleResetPassword = async () => {
    if (!showResetPw || !newPassword) return;
    try {
      await resetPassword.mutateAsync({ id: showResetPw, password: newPassword });
      toast({ title: "Password reset" });
      setShowResetPw(null);
      setNewPassword("");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAssignCompanies = async () => {
    if (!showAssign) return;
    try {
      await assignCompanies.mutateAsync({ staffId: showAssign, companyIds: selectedCompanyIds });
      toast({ title: "Companies assigned" });
      setShowAssign(null);
      setSelectedCompanyIds([]);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Staff Management</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your team members and their access
              {planLimit && <span className="ml-2">({planLimit.current}/{planLimit.max} staff)</span>}
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} disabled={planLimit && !planLimit.allowed}>
            <Plus className="w-4 h-4 mr-2" />
            Add Staff
          </Button>
        </div>

        {/* Staff list */}
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : staff.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-1">No staff members yet</h3>
              <p className="text-muted-foreground mb-4">Add your first team member to get started</p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Staff
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-sm font-medium">Email</th>
                      <th className="text-left p-3 text-sm font-medium">Role</th>
                      <th className="text-left p-3 text-sm font-medium">Status</th>
                      <th className="text-left p-3 text-sm font-medium">Companies</th>
                      <th className="text-right p-3 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {staff.map((s) => (
                      <tr key={s.id} className="hover:bg-muted/30">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                              {(s.first_name?.[0] || s.email[0]).toUpperCase()}
                            </div>
                            <span className="font-medium">{s.full_name || s.email}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{s.email}</td>
                        <td className="p-3">
                          <Badge variant={s.role === "admin" ? "default" : "secondary"} className="capitalize">
                            {s.role}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant={s.is_active ? "default" : "destructive"}>
                            {s.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm">{s.company_count}</td>
                        <td className="p-3 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/staff/${s.id}/profile`)} title="View Profile">
                            <UserCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowAssign(s.id); setSelectedCompanyIds([]); }} title="Assign Companies">
                            <Building2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowResetPw(s.id); setNewPassword(""); }} title="Reset Password">
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          {s.is_active ? (
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeactivate(s.id)} title="Deactivate">
                              <UserX className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleReactivate(s.id)} title="Reactivate" disabled={reactivateStaff.isPending}>
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Staff Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name</Label><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></div>
                <div><Label>Last Name</Label><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {businesses.length > 0 && (
                <div>
                  <Label>Assign Companies</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                    {businesses.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.companyIds?.includes(b.id) || false}
                          onChange={(e) => {
                            const ids = form.companyIds || [];
                            setForm({ ...form, companyIds: e.target.checked ? [...ids, b.id] : ids.filter((i) => i !== b.id) });
                          }}
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={createStaff.isPending}>
                {createStaff.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reset Staff Password</DialogTitle></DialogHeader>
            <div><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetPw(null)}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={resetPassword.isPending || newPassword.length < 6}>Reset</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Companies Dialog */}
        <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Assign Companies</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {businesses.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded hover:bg-muted">
                  <input
                    type="checkbox"
                    checked={selectedCompanyIds.includes(b.id)}
                    onChange={(e) => setSelectedCompanyIds(e.target.checked ? [...selectedCompanyIds, b.id] : selectedCompanyIds.filter((i) => i !== b.id))}
                  />
                  {b.name}
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssign(null)}>Cancel</Button>
              <Button onClick={handleAssignCompanies} disabled={assignCompanies.isPending}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
